import React, { useContext, useEffect, useState } from 'react';
import { Container, Row, Col, Image, Button, InputGroup, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as Icon from 'react-bootstrap-icons';
import { Card, CardContent, CardActions, Typography, Avatar, Box, Grid, Divider, Chip, Paper, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import ApiFetch from './ApiFetch';
import FollowButton from './FollowButton';
import UserMark from './UserMark';
import { LoginInfo } from '../App';
import { useTranslation } from "react-i18next";

// スタイル付きコンポーネント
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 90,
  height: 90,
  margin: '0 auto 16px',
  border: '3px solid #f0f0f0',
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const UserNameText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: '8px',
  color: theme.palette.text.primary,
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const IntroductionText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
  display: '-webkit-box',
  '-webkit-line-clamp': 3,
  '-webkit-box-orient': 'vertical',
  overflow: 'hidden',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '24px',
  padding: '16px 0',
  borderBottom: '2px solid #e0e0e0',
  position: 'sticky',
  top: 0,
  backgroundColor: '#ffffff',
  zIndex: 10,
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  fontSize: '0.875rem',
}));

const FollowFollowerList = ({ 
  followList = [], 
  followerList = [], 
  followCount = 0, 
  followerCount = 0,
  activeTab = 'follow',
  onTabChange,
  observer,
  followPage,
  followerPage,
  onLoadMore,
  onUserClick // ユーザークリック時のコールバック
}) => {
  const { t } = useTranslation();
  const { loginInfo } = useContext(LoginInfo);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(isMobile ? 'list' : 'grid'); // モバイルではデフォルトでリスト表示

  // モバイル状態が変更された時にviewModeを調整
  useEffect(() => {
    if (isMobile && viewMode === 'grid') {
      setViewMode('list');
    }
  }, [isMobile, viewMode]);

  // observer要素をセットアップ
  useEffect(() => {
    if (observer) {
      const lazyElements = document.querySelectorAll('.lazy');
      lazyElements.forEach(el => {
        observer.observe(el);
      });

      return () => {
        lazyElements.forEach(el => {
          observer.unobserve(el);
        });
      };
    }
  }, [observer, followList, followerList, activeTab, viewMode]);

  // フィルタリング関数
  const filterUsers = (users) => {
    if (!searchTerm) return users;
    return users.filter(user => 
      user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.self_introduction?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredFollowList = filterUsers(followList);
  const filteredFollowerList = filterUsers(followerList);
  const currentList = activeTab === 'follow' ? filteredFollowList : filteredFollowerList;

  // ユーザーカードコンポーネント
  const UserCard = ({ user, index, type }) => (
    <Grid item xs={12} sm={6} md={6} lg={4} key={user.follow_user_id || user.user_id}>
      <StyledCard>
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          <Link 
            to={`/user/${user.follow_user_id || user.user_id}`}
            style={{ textDecoration: 'none' }}
            onClick={onUserClick} // ダイアログを閉じる
          >
            <UserAvatar
              src={`${ApiFetch.API_PROTOCOL}//${ApiFetch.IMG_HOST}/profile/${user.follow_user_id || user.user_id}.jpg`}
              alt={user.user_name}
              data-type={type}
              data-observe="0"
              data-no={index}
              className="lazy"
              loading="lazy"
            />
          </Link>
          
          <Link 
            to={`/user/${user.follow_user_id || user.user_id}`}
            style={{ textDecoration: 'none' }}
            onClick={onUserClick} // ダイアログを閉じる
          >
            <UserNameText variant="h6">
              {user.user_name}
              <UserMark plan={user.plan} />
            </UserNameText>
          </Link>

          {user.self_introduction && (
            <IntroductionText variant="body2">
              {user.self_introduction}
            </IntroductionText>
          )}

          {/* 統計情報表示 */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {user.image_count && (
              <Chip 
                size="small" 
                label={`投稿 ${user.image_count}`} 
                variant="outlined"
                color="primary"
              />
            )}
            {user.follow_count && (
              <Chip 
                size="small" 
                label={`フォロー ${user.follow_count}`} 
                variant="outlined"
                color="secondary"
              />
            )}
            {user.follower_count && (
              <Chip 
                size="small" 
                label={`フォロワー ${user.follower_count}`} 
                variant="outlined"
                color="secondary"
              />
            )}
          </Box>
        </CardContent>
        
        <CardActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
          {loginInfo.userId !== (user.follow_user_id || user.user_id) && (
            <FollowButton 
              userId={user.follow_user_id || user.user_id} 
              follow={user.follow} 
              userName={user.user_name} 
            />
          )}
        </CardActions>
      </StyledCard>
    </Grid>
  );

  // リスト表示モード
  const UserListItem = ({ user, index, type }) => (
    <Paper sx={{ mb: 2, p: 2, borderRadius: 2 }} key={user.follow_user_id || user.user_id}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Link to={`/user/${user.follow_user_id || user.user_id}`} onClick={onUserClick}>
          <Avatar
            src={`${ApiFetch.API_PROTOCOL}//${ApiFetch.IMG_HOST}/profile/${user.follow_user_id || user.user_id}.jpg`}
            alt={user.user_name}
            sx={{ width: 60, height: 60 }}
            data-type={type}
            data-observe="0"
            data-no={index}
            className="lazy"
            loading="lazy"
          />
        </Link>
        
        <Box sx={{ flexGrow: 1 }}>
          <Link 
            to={`/user/${user.follow_user_id || user.user_id}`}
            style={{ textDecoration: 'none' }}
            onClick={onUserClick} // ダイアログを閉じる
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
              {user.user_name}
              <UserMark plan={user.plan} />
            </Typography>
          </Link>
          
          {user.self_introduction && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {user.self_introduction}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {user.image_count && (
              <Chip size="small" label={`投稿 ${user.image_count}`} variant="outlined" />
            )}
            {user.follow_count && (
              <Chip size="small" label={`フォロー ${user.follow_count}`} variant="outlined" />
            )}
            {user.follower_count && (
              <Chip size="small" label={`フォロワー ${user.follower_count}`} variant="outlined" />
            )}
          </Box>
        </Box>
        
        <Box>
          {loginInfo.userId !== (user.follow_user_id || user.user_id) && (
            <FollowButton 
              userId={user.follow_user_id || user.user_id} 
              follow={user.follow} 
              userName={user.user_name} 
            />
          )}
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Container 
      maxWidth={isMobile ? false : "lg"} 
      sx={{ 
        py: isMobile ? 1 : 3,
        px: isMobile ? 1 : 3,
        height: isMobile ? '100%' : 'auto',
        overflow: isMobile ? 'auto' : 'visible'
      }}
    >
      {/* ヘッダーセクション */}
      <SectionHeader>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 1 : 2, 
          flexGrow: 1,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon.People size={isMobile ? 24 : 28} color="#1976d2" />
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {activeTab === 'follow' ? t("common.follow") : t("common.follower")}
            </Typography>
            <StatsChip 
              label={activeTab === 'follow' ? `${followCount}人` : `${followerCount}人`}
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>
        
        {/* 表示モード切り替え */}
        <Box sx={{ display: 'flex', gap: 1, mt: isMobile ? 1 : 0 }}>
          <Button
            variant={viewMode === 'grid' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('grid')}
            startIcon={<Icon.Grid3x3Gap />}
          >
            {isMobile ? '' : 'グリッド'}
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('list')}
            startIcon={<Icon.List />}
          >
            {isMobile ? '' : 'リスト'}
          </Button>
        </Box>
      </SectionHeader>

      {/* 検索・フィルター */}
      <Box sx={{ mb: 3 }}>
        <Row>
          <Col xs={12} md={6} lg={4}>
            <InputGroup>
              <InputGroup.Text>
                <Icon.Search />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={isMobile ? "検索..." : "ユーザー名や自己紹介で検索..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>
      </Box>

      {/* タブ切り替え */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant={activeTab === 'follow' ? 'contained' : 'outlined'}
          onClick={() => onTabChange('follow')}
          sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0 }}
          startIcon={<Icon.PersonPlus />}
          size={isMobile ? "small" : "medium"}
        >
          {t("common.follow")} ({followCount})
        </Button>
        <Button
          variant={activeTab === 'follower' ? 'contained' : 'outlined'}
          onClick={() => onTabChange('follower')}
          startIcon={<Icon.PersonHeart />}
          size={isMobile ? "small" : "medium"}
        >
          {t("common.follower")} ({followerCount})
        </Button>
      </Box>

      {/* ユーザーリスト表示 */}
      {currentList.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Icon.PersonX size={64} color="#ccc" />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            {searchTerm 
              ? '検索条件に一致するユーザーが見つかりません' 
              : activeTab === 'follow' 
                ? 'まだフォローしているユーザーがいません' 
                : 'まだフォロワーがいません'
            }
          </Typography>
          {searchTerm && (
            <Button 
              variant="outlined" 
              onClick={() => setSearchTerm('')}
              sx={{ mt: 2 }}
            >
              検索をクリア
            </Button>
          )}
        </Box>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <Grid container spacing={isMobile ? 1 : 2} sx={{ justifyContent: 'flex-start' }}>
              {currentList.map((user, index) => (
                <UserCard 
                  key={user.follow_user_id || user.user_id} 
                  user={user} 
                  index={index} 
                  type={activeTab}
                />
              ))}
            </Grid>
          ) : (
            <Box>
              {currentList.map((user, index) => (
                <UserListItem 
                  key={user.follow_user_id || user.user_id} 
                  user={user} 
                  index={index} 
                  type={activeTab}
                />
              ))}
            </Box>
          )}

          {/* 検索結果の統計 */}
          {searchTerm && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {currentList.length}件の検索結果
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* 無限スクロール用の最後の要素 */}
      {currentList.length > 0 && (
        <div 
          data-type={activeTab} 
          data-observe="0" 
          data-no={currentList.length - 1}
          className="lazy"
          style={{ height: '1px', marginTop: '20px' }}
        />
      )}
    </Container>
  );
};

export default FollowFollowerList;
